import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import createRive from '../utilities/createRive';

gsap.registerPlugin(CustomEase);
gsap.registerPlugin(ScrollTrigger);

const STATE_MACHINES_NAME = 'ep240k';
const FPS_VALUE = 50;

CustomEase.create('resize', '.22,0,.75,1');

const setup = () => {
  const stepScroller = document.querySelectorAll('.w-b-step-scroller');
  if (stepScroller) {
    stepScroller.forEach((scroller) => {
      gsap.to(scroller, {
        scrollTrigger: {
          trigger: scroller,
          start: 'top top-=1px',
          // end: 'bottom bottom',
          fastScrollEnd: true,
          onEnter: () => {
            scroller.classList.add('w-is-started');
          },
          // onLeave: () => {
          //   scroller.classList.remove('w-is-started');
          // },
        },
      });
    });
  }

  const triggers = document.querySelectorAll('[data-w-scene]');

  let mediaManager = gsap.matchMedia();

  // edge: 768px
  // xl: 1024px
  // ipadpro height

  mediaManager.add(
    {
      isMobile: `(max-width: 768px), (min-width: 768px) and (max-width: 1366px) and (orientation: portrait)`,
      isDesktop: `(min-width: 768px) and (max-width: 1366px) and (orientation: landscape), (min-width: 1366px)`,
    },
    (context) => {
      let { isDesktop, isMobile } = context.conditions;

      // if (isDesktop) {
      //   document.documentElement.classList.add('desktop');
      //   document.documentElement.classList.remove('mobile');
      // } else if (isMobile) {
      //   document.documentElement.classList.add('mobile');
      //   document.documentElement.classList.remove('desktop');
      // }

      if (isDesktop) {
        triggers.forEach((trigger) => {
          gsap.set(trigger, { '--_size': 0 }); // for full width slides
          gsap.fromTo(
            trigger,
            {
              '--_progress': 1,
            },
            {
              scrollTrigger: {
                trigger: trigger,
                start: 'top 50%',
                end: () => `+=${window.innerHeight * 0.5}`,
                scrub: true,
                fastScrollEnd: true,
                onUpdate: (self) => {
                  trigger.style.setProperty('--_progress', 1 - self.progress);
                },
                onLeave: () => {
                  trigger.classList.add('w-is-entered');
                },
                onEnterBack: () => {
                  trigger.classList.remove('w-is-entered');
                },
              },
            }
          );
          gsap.to(trigger, {
            scrollTrigger: {
              trigger: trigger,
              start: 'bottom bottom',
              end: () => `+=${window.innerHeight * 0.5}`,
              scrub: true,
              fastScrollEnd: true,
              onUpdate: (self) => {
                trigger.style.setProperty('--_progress', self.progress);
              },
              onEnter: () => {
                trigger.classList.remove('w-is-entered');
              },
              onLeaveBack: () => {
                trigger.classList.add('w-is-entered');
              },
            },
          });

          const steps = trigger.querySelectorAll('[data-w-scene-step]');
          const stepsLength = steps.length;
          let previousMediaSize;
          steps.forEach((step, stepIndex) => {
            const index = step.getAttribute('data-w-scene-step');
            const media = trigger.querySelector(
              `[data-w-scene-media="${index}"]`
            );

            const stepInner = step.querySelector('[data-w-scene-step-inner]');
            if (stepIndex < stepsLength - 1) {
              gsap.to(stepInner, {
                y: '+75%',
                opacity: 0,
                scale: 0.875,
                ease: 'power1.in',
                scrollTrigger: {
                  trigger: step,
                  start: 'center 75%',
                  end: 'bottom 12.5%',
                  scrub: true,
                  fastScrollEnd: true,
                },
              });
            }

            if (stepInner) {
              const stepInnerSections = stepInner.querySelectorAll(
                '[data-w-step-section]'
              );
              Array.from(stepInnerSections)
                .slice(0, -1)
                .forEach((stepInnerSection) => {
                  gsap.to(stepInnerSection, {
                    y: '+75%',
                    opacity: 0,
                    scale: 0.875,
                    ease: 'power1.in',
                    scrollTrigger: {
                      trigger: stepInnerSection,
                      start: 'center 75%',
                      end: 'bottom 12.5%',
                      scrub: true,
                      fastScrollEnd: true,
                    },
                  });
                });
            }

            const mediaSize =
              media.getAttribute('data-w-scene-media-size') ?? 'default';
            if (stepIndex === 0) previousMediaSize = mediaSize;
            const currentPreviousMediaSize = previousMediaSize;
            previousMediaSize = mediaSize;
            const sizeIsChanging = mediaSize !== currentPreviousMediaSize;

            const drag = media.hasAttribute('data-w-scene-drag') ? 2 : true;
            const parallax = media.hasAttribute('data-w-scene-parallax');
            const sync = media.hasAttribute('data-w-scene-sync');
            // const syncType = media.getAttribute('data-w-scene-sync') ?? false;
            const color = media.getAttribute('data-w-scene-color') ?? false;

            const video = media.querySelector('video');
            let videoLoaded = false;
            if (video) {
              video.load();
              video.pause();
              video.addEventListener('loadedmetadata', () => {
                video.removeAttribute('autoplay');
                video.currentTime = 0.001;
                video.pause();
                videoLoaded = true;
              });
            }
            const videoMode =
              media.getAttribute('data-w-scene-video-mode') ?? 'control';

            const image = media.querySelector('img');
            const canvas = media.querySelector('canvas');

            const stepContent = trigger.querySelector(
              `[data-w-scene-step="${index}"]`
            );

            const rive = canvas
              ? createRive(canvas, stepContent, STATE_MACHINES_NAME)
              : null;

            if (stepIndex === 0 && !video && rive?.r) {
              rive.r.startRendering();
              rive.r.play(STATE_MACHINES_NAME, true);
            }

            let triggerStart =
              videoMode === 'control'
                ? stepsLength > 1 && stepIndex === 0
                  ? 'top top'
                  : 'top 80%'
                : 'top bottom';
            if (stepIndex > 0 && sizeIsChanging) triggerStart = 'top bottom';
            let triggerEnd =
              videoMode === 'control'
                ? stepsLength > 1 && stepIndex === 0
                  ? 'top top'
                  : 'top 20%'
                : 'bottom top';

            // scrollTrigger for video playback control
            gsap.to(step, {
              scrollTrigger: {
                trigger: step,
                start: triggerStart,
                end: triggerEnd,
                fastScrollEnd: true,
                scrub: drag,
                onUpdate: (self) => {
                  if (sizeIsChanging)
                    trigger.style.setProperty(
                      '--_size',
                      0 + gsap.parseEase('resize')(self.progress)
                    );

                  if (videoLoaded && videoMode === 'control') {
                    const time =
                      Math.floor(self.progress * video.duration * FPS_VALUE) /
                      FPS_VALUE;
                    video.currentTime = time;
                  }
                },
                onEnter: () => {
                  if (video && videoMode !== 'control') video.play();

                  media.classList.add('w-is-active'); // add only when scrolling down
                },
                onLeave: () => {
                  if (video && videoMode !== 'control') video.pause();
                  else {
                    if (image) image.classList.add('w-is-visible');

                    if (canvas) canvas.classList.add('w-is-visible');
                    if (rive?.r) {
                      rive.r.startRendering();
                      rive.r.play(STATE_MACHINES_NAME, true);
                    }
                  }
                },
                onEnterBack: () => {
                  if (video && videoMode !== 'control') video.play();
                  else {
                    if (image) image.classList.remove('w-is-visible');

                    if (canvas) canvas.classList.remove('w-is-visible');
                    // if (rive?.r) rive.r.stop(STATE_MACHINES_NAME);
                  }
                },
                onLeaveBack: () => {
                  if (video && videoMode !== 'control') video.pause();

                  media.classList.remove('w-is-active'); // remove only when scrolling up
                },
              },
            });

            // scrollTrigger for parallax effect
            if (parallax) {
              gsap.to(step, {
                scrollTrigger: {
                  trigger: step,
                  start: 'top bottom',
                  end: 'bottom top',
                  scrub: true,
                  onUpdate: (self) => {
                    trigger.style.setProperty('--_scroll', self.progress);
                  },
                },
              });
            }

            if (sync) {
              // scrollTrigger for sync effect
              gsap.set(trigger, { '--_sync': '0%' });
              gsap.to(step, {
                scrollTrigger: {
                  trigger: step,
                  start: 'top top',
                  end: 'bottom bottom',
                  scrub: true,
                  onUpdate: (self) => {
                    const easedProgress = self.progress; // gsap.parseEase('sine.inOut')(self.progress);
                    trigger.style.setProperty(
                      '--_sync',
                      `${easedProgress * 100}%`
                    );
                  },
                },
              });
              // gsap.to(step, {
              //   scrollTrigger: {
              //     trigger: step,
              //     start: 'top bottom',
              //     end: () => `+=${window.innerHeight * 2}`,
              //     scrub: true,
              //     onUpdate: (self) => {
              //       trigger.style.setProperty('--_factor', self.progress);
              //     },
              //   },
              // });
            }

            if (color) {
              // scrollTrigger for background color
              gsap.to(step, {
                scrollTrigger: {
                  trigger: step,
                  start: 'top bottom',
                  end: () => `+=${window.innerHeight * 0.5}`,
                  scrub: true,
                  onUpdate: (self) => {
                    trigger.style.setProperty(
                      '--_color-progress',
                      `${self.progress * 100}%`
                    );
                    // trigger.style.setProperty(
                    //   '--_color-value',
                    //   `var(--color-scheme-${color})`
                    // );
                  },
                },
              });
            }

            // scrollTrigger for rive playback control
            if (rive?.r) {
              gsap.to(step, {
                scrollTrigger: {
                  trigger: canvas,
                  start: 'top bottom',
                  end: 'bottom top',
                  fastScrollEnd: true,
                  onEnter: () => {
                    rive.r.startRendering();
                    rive.r.play(STATE_MACHINES_NAME, true);
                  },
                  onEnterBack: () => {
                    rive.r.startRendering();
                    rive.r.play(STATE_MACHINES_NAME, true);
                  },
                  onLeaveBack: () => {
                    rive.r.stop(STATE_MACHINES_NAME);
                    setTimeout(() => {
                      rive.r.stopRendering();
                    }, 1);
                  },
                  onLeave: () => {
                    rive.r.stop(STATE_MACHINES_NAME);
                    setTimeout(() => {
                      rive.r.stopRendering();
                    }, 1);
                  },
                },
              });
            }
          });
        });
      } else if (isMobile) {
        triggers.forEach((trigger) => {
          const steps = trigger.querySelectorAll('[data-w-scene-step]');
          const isStacked =
            trigger.getAttribute('data-w-scene-stack-mobile') === 'true';

          steps.forEach((step, stepIndex) => {
            const index = step.getAttribute('data-w-scene-step');
            const media = trigger.querySelector(
              `[data-w-scene-media="${index}"]`
            );

            const mediaSize =
              media.getAttribute('data-w-scene-media-size') ?? 'default';

            const video = media.querySelector('video');
            let videoLoaded = false;
            if (video) {
              video.load();
              video.pause();
              video.addEventListener('loadedmetadata', () => {
                video.removeAttribute('autoplay');
                video.currentTime = 0.001;
                video.pause();
                videoLoaded = true;
              });
            }
            const videoMode =
              media.getAttribute('data-w-scene-video-mode') ?? 'control';

            const image = media.querySelector('img');
            const canvas = media.querySelector('canvas');

            const stepContent = trigger.querySelector(
              `[data-w-scene-step="${index}"]`
            );

            const rive = canvas
              ? createRive(canvas, stepContent, STATE_MACHINES_NAME)
              : null;

            // scrollTrigger for video playback control
            gsap.to(step, {
              scrollTrigger: {
                trigger: step,
                start: 'top bottom',
                end: 'bottom top',
                fastScrollEnd: true,
                onEnter: () => {
                  if (video && videoMode !== 'control') video.play();
                  if (rive?.r) {
                    rive.r.startRendering();
                    rive.r.play(STATE_MACHINES_NAME, true);
                  }
                },
                onLeave: () => {
                  if (video && videoMode !== 'control') video.pause();
                },
                onEnterBack: () => {
                  if (video && videoMode !== 'control') video.play();
                  if (rive?.r) {
                    rive.r.startRendering();
                    rive.r.play(STATE_MACHINES_NAME, true);
                  }
                },
                onLeaveBack: () => {
                  if (video && videoMode !== 'control') video.pause();
                },
              },
            });

            // step media in/out
            gsap.to(step, {
              scrollTrigger: {
                trigger: step,
                start: 'top 75%',
                // end: 'bottom top',
                fastScrollEnd: true,
                onEnter: () => {
                  media.classList.add('w-is-active'); // add only when scrolling down
                },
                onLeaveBack: () => {
                  media.classList.remove('w-is-active'); // remove only when scrolling up
                },
                // markers: true,
              },
            });

            if (isStacked && mediaSize === 'full') {
              gsap.to(step, {
                scrollTrigger: {
                  trigger: step,
                  start: 'top bottom',
                  end: 'bottom top',
                  scrub: true,
                  onUpdate: (self) => {
                    trigger.style.setProperty('--_scroll', self.progress);
                  },
                },
              });
            }

            // scrollTrigger for rive playback control
            if (rive?.r) {
              gsap.to(step, {
                scrollTrigger: {
                  trigger: canvas,
                  start: 'top bottom',
                  end: 'bottom top',
                  fastScrollEnd: true,
                  onEnter: () => {
                    rive.r.startRendering();
                    rive.r.play(STATE_MACHINES_NAME, true);
                  },
                  onEnterBack: () => {
                    rive.r.startRendering();
                    rive.r.play(STATE_MACHINES_NAME, true);
                  },
                  onLeaveBack: () => {
                    rive.r.stop(STATE_MACHINES_NAME);
                    setTimeout(() => {
                      rive.r.stopRendering();
                    }, 1);
                  },
                  onLeave: () => {
                    rive.r.stop(STATE_MACHINES_NAME);
                    setTimeout(() => {
                      rive.r.stopRendering();
                    }, 1);
                  },
                },
              });
            }
          });
        });
      }

      return () => {};
    }
  );
};

export default function StepScroller() {
  return {
    init: () => {
      setup();
    },
  };
}
